import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Check, Star, MapPin, Shield, Sparkles } from 'lucide-react';
import axios from 'axios';
import { LandingSwitcher } from '../components/LandingSwitcher';
import { FAVICON } from '../assets';
import './landing-vip.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ShopperLandingPage = () => {
  const { i18n } = useTranslation();
  const isSpanish = i18n.language?.startsWith('es');
  const lang = i18n.language?.substring(0, 2) || 'es';
  const [openFaq, setOpenFaq] = useState(null);
  const [userCount, setUserCount] = useState(0);

  useEffect(() => {
    const fetchUserCount = async () => {
      try {
        const { data } = await axios.get(`${API}/stats/user-count`);
        setUserCount(data.count || 0);
      } catch (error) {
        console.error('Error fetching user count:', error);
        setUserCount(0); // Real data only, no fake fallback
      }
    };
    
    // Fetch initially
    fetchUserCount();
    
    // Refresh every 30 seconds for real-time updates
    const interval = setInterval(fetchUserCount, 30000);
    
    return () => clearInterval(interval);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const content = {
    es: {
      // Hero
      heroEyebrow: "Club de Recompensas VIP",
      heroTitle: <>¿Sabías que tu barrio<br/>te debe un viaje a París<br/>o a Canarias?</>,
      heroTitleAccent: "Es hora de reclamar tu llave.",
      heroSub: <>El consumidor medio gasta entre <strong>3.000€ y 6.000€ al año</strong> en comercios locales. En LokAlGrow, auditamos tu lealtad y convertimos ese gasto invisible en estancias de lujo con pensión completa en hoteles de 4 y 5 estrellas. <em>Sin cuotas, sin intermediarios.</em></>,
      ctaPrimary: "Reclamar mi Estatus VIP — Es Gratis",
      ctaSecondary: "Descubrir cómo funciona",
      
      // Counter
      counterLabel: "Objetivo de lanzamiento",
      counterProgress: "compradores registrados",
      counterTarget: "50.000",
      counterUnlock: "Los canjes de puntos se activan al alcanzar 50.000 compradores",
      counterNote: "¡Únete ahora y acumula puntos desde el primer día!",
      
      // Differentiator
      diffTitle: "No somos otra plataforma de cupones.",
      diffSub: "No asfixiamos al comercio local. Tú pagas al dueño del negocio, nosotros premiamos tu impacto en la comunidad.",
      
      // Spending Section
      spendingEyebrow: "La matemática de tu fidelidad",
      spendingTitle: <>Cada euro que gastas<br/>en tu barrio es un activo.<br/><span>Deja de regalarlo.</span></>,
      spendingBody: <>Sin LokAlGrow, tu gasto en comercios locales desaparece sin dejar rastro. Miles de euros al año que se evaporan sin darte nada a cambio.<br/><br/><strong>Con LokAlGrow, cada compra construye tu próximo viaje.</strong> Acumulas valor automáticamente y lo transformas en experiencias que normalmente no te permitirías.</>,
      spendingCards: [
        { amount: "~1.500€/año", label: "Alimentación local", desc: "Tu frutería, carnicería, panadería de siempre" },
        { amount: "~900€/año", label: "Restaurantes", desc: "Las cenas, cafés y comidas de barrio" },
        { amount: "~480€/año", label: "Moda local", desc: "Boutiques y tiendas independientes" },
        { amount: "~300€/año", label: "Servicios", desc: "Peluquería, estética, bienestar" }
      ],
      spendingTotal: "+3.000€",
      spendingTotalLabel: "de gasto anual que podrías estar convirtiendo en vacaciones",
      
      // Process
      processEyebrow: "El Proceso",
      processTitle: <>Tres pasos hacia<br/>tu próximo destino.</>,
      processSub: "Sin complicaciones. Sin apps que olvidas. Sin puntos que caducan.",
      steps: [
        { 
          num: "01", 
          title: "Reserva tu Pase VIP", 
          desc: "Encuentra a los comercios que valoran tu fidelidad. Activa ofertas exclusivas de hasta 72 horas de duración.",
          detail: "Cada oferta es una oportunidad única"
        },
        { 
          num: "02", 
          title: "Disfruta de tu Barrio", 
          desc: "Paga directamente en el local. Sin intermediarios, sin comisiones ocultas. Tu dinero va íntegro al comerciante.",
          detail: "Apoyo directo al comercio local"
        },
        { 
          num: "03", 
          title: "Sube tu Ticket", 
          desc: "Nuestra IA valida tu inversión local y te acerca a tu próximo destino. Cada compra suma, cada ticket cuenta.",
          detail: "Tecnología que trabaja para ti"
        }
      ],
      
      // Rewards
      rewardsEyebrow: "Tus Recompensas",
      rewardsTitle: <>Donde tu lealtad<br/>te puede llevar.</>,
      rewardsSub: "No hablamos de vales de 2€. Hablamos de experiencias que recordarás toda la vida.",
      rewardsNote: "Tú eliges cómo llegar, nosotros nos ocupamos de tu estancia. Premios incluyen pensión completa en hoteles seleccionados de 4 y 5 estrellas. Vuelos no incluidos.",
      rewards: [
        { 
          destination: "París", 
          nights: "3 Noches",
          hotel: "Hotel 4★ con pensión completa",
          image: "https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=600&q=80",
          tag: "El más solicitado"
        },
        { 
          destination: "Canarias", 
          nights: "3 Noches",
          hotel: "Resort 4★ frente al mar",
          image: "https://images.unsplash.com/photo-1590523741831-ab7e8b8f9c7f?w=600&q=80",
          tag: "Paraíso cercano"
        },
        { 
          destination: "Europa", 
          nights: "7 Noches",
          hotel: "Tu elección de destino",
          image: "https://images.unsplash.com/photo-1499856871958-5b9627545d1a?w=600&q=80",
          tag: "La experiencia definitiva"
        }
      ],
      
      // Social Proof
      proofEyebrow: "La Filosofía",
      proofQuote: "\"Al no usar LokAlGrow, estás perdiendo dinero que ya estás gastando de todas formas.\"",
      proofAuthor: "— El principio que cambia todo",
      
      // FAQ
      faqEyebrow: "Preguntas Frecuentes",
      faqTitle: <>Transparencia<br/>absoluta.</>,
      faqs: [
        { 
          q: "¿Realmente es gratis?", 
          a: <>Absolutamente. <strong>LokAlGrow es y será siempre 100% gratuito para consumidores.</strong> Nuestro modelo de negocio se basa en ayudar a los comercios locales a atraer clientes, no en cobrarte a ti.</> 
        },
        { 
          q: "¿Qué incluyen exactamente los premios?", 
          a: <>Estancias con <strong>pensión completa</strong> (alojamiento, desayuno, almuerzo y cena) en hoteles de 4 y 5 estrellas seleccionados. El transporte hasta el destino corre por tu cuenta, lo que te da flexibilidad total para elegir cómo y cuándo viajar.</> 
        },
        { 
          q: "¿Mis puntos caducan?", 
          a: <>No. <strong>Tus puntos son tuyos para siempre.</strong> Acumula a tu ritmo, sin presión, y canjea cuando tú decidas.</> 
        },
        { 
          q: "¿En qué se diferencia de otras plataformas de descuentos?", 
          a: <>En todo. Las plataformas de cupones tradicionales cobran comisiones enormes que asfixian a los comercios. <strong>En LokAlGrow, tú pagas directamente al dueño del negocio.</strong> Nosotros premiamos tu impacto en la economía local, no extraemos valor de ella.</> 
        },
        { 
          q: "¿Cómo verificáis mis compras?", 
          a: <>Nuestra <strong>tecnología de IA analiza los tickets</strong> que subes para validar tus compras de forma segura y privada. Es rápido, preciso y completamente automático.</> 
        }
      ],
      
      // Final CTA
      finalEyebrow: "Tu Decisión",
      finalTitle: <>El gasto ya está hecho.<br/>Las vacaciones, todavía no.</>,
      finalSub: "Únete a miles de consumidores conscientes que han dejado de regalar su lealtad y han empezado a recuperarla.",
      finalCta: "Empezar a Acumular mis Vacaciones",
      finalNote: "Sin tarjeta de crédito · Sin compromiso · Siempre gratis"
    },
    en: {
      // Hero
      heroEyebrow: "VIP Rewards Club",
      heroTitle: <>Did you know your<br/>neighbourhood owes you<br/>a trip to Paris or the Canaries?</>,
      heroTitleAccent: "It's time to claim your key.",
      heroSub: <>The average consumer spends between <strong>€3,000 and €6,000 a year</strong> at local businesses. At LokAlGrow, we audit your loyalty and convert that invisible spending into luxury full-board stays at 4 and 5-star hotels. <em>No fees, no middlemen.</em></>,
      ctaPrimary: "Claim my VIP Status — It's Free",
      ctaSecondary: "Discover how it works",
      
      // Counter
      counterLabel: "Launch goal",
      counterProgress: "shoppers registered",
      counterTarget: "50,000",
      counterUnlock: "Point redemptions unlock at 50,000 shoppers",
      counterNote: "Join now and start earning points from day one!",
      
      // Differentiator
      diffTitle: "Not another coupon platform.",
      diffSub: "We don't squeeze local businesses. You pay the shop owner directly, we reward your community impact.",
      
      // Spending Section
      spendingEyebrow: "The maths of your loyalty",
      spendingTitle: <>Every euro you spend<br/>locally is an asset.<br/><span>Stop giving it away.</span></>,
      spendingBody: <>Without LokAlGrow, your local spending disappears without a trace. Thousands of euros a year that evaporate without giving you anything in return.<br/><br/><strong>With LokAlGrow, every purchase builds your next trip.</strong> You accumulate value automatically and transform it into experiences you wouldn't normally allow yourself.</>,
      spendingCards: [
        { amount: "~€1,500/year", label: "Local food", desc: "Your usual greengrocer, butcher, bakery" },
        { amount: "~€900/year", label: "Restaurants", desc: "Neighbourhood dinners, coffees and meals" },
        { amount: "~€480/year", label: "Local fashion", desc: "Boutiques and independent shops" },
        { amount: "~€300/year", label: "Services", desc: "Hairdresser, beauty, wellness" }
      ],
      spendingTotal: "€3,000+",
      spendingTotalLabel: "annual spending you could be converting into holidays",
      
      // Process
      processEyebrow: "The Process",
      processTitle: <>Three steps to<br/>your next destination.</>,
      processSub: "No complications. No apps you forget. No points that expire.",
      steps: [
        { 
          num: "01", 
          title: "Reserve your VIP Pass", 
          desc: "Find businesses that value your loyalty. Activate exclusive offers lasting up to 72 hours.",
          detail: "Each offer is a unique opportunity"
        },
        { 
          num: "02", 
          title: "Enjoy your Neighbourhood", 
          desc: "Pay directly at the shop. No middlemen, no hidden fees. Your money goes entirely to the merchant.",
          detail: "Direct support for local commerce"
        },
        { 
          num: "03", 
          title: "Upload your Receipt", 
          desc: "Our AI validates your local investment and brings you closer to your next destination. Every purchase counts.",
          detail: "Technology working for you"
        }
      ],
      
      // Rewards
      rewardsEyebrow: "Your Rewards",
      rewardsTitle: <>Where your loyalty<br/>can take you.</>,
      rewardsSub: "We're not talking about €2 vouchers. We're talking about experiences you'll remember forever.",
      rewardsNote: "You choose how to get there, we take care of your stay. Rewards include full board at selected 4 and 5-star hotels. Flights not included.",
      rewards: [
        { 
          destination: "Paris", 
          nights: "3 Nights",
          hotel: "4★ Hotel with full board",
          image: "https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=600&q=80",
          tag: "Most requested"
        },
        { 
          destination: "Canary Islands", 
          nights: "3 Nights",
          hotel: "4★ Beachfront resort",
          image: "https://images.unsplash.com/photo-1590523741831-ab7e8b8f9c7f?w=600&q=80",
          tag: "Paradise nearby"
        },
        { 
          destination: "Europe", 
          nights: "7 Nights",
          hotel: "Your choice of destination",
          image: "https://images.unsplash.com/photo-1499856871958-5b9627545d1a?w=600&q=80",
          tag: "The ultimate experience"
        }
      ],
      
      // Social Proof
      proofEyebrow: "The Philosophy",
      proofQuote: "\"By not using LokAlGrow, you're losing money you're already spending anyway.\"",
      proofAuthor: "— The principle that changes everything",
      
      // FAQ
      faqEyebrow: "FAQ",
      faqTitle: <>Absolute<br/>transparency.</>,
      faqs: [
        { 
          q: "Is it really free?", 
          a: <>Absolutely. <strong>LokAlGrow is and will always be 100% free for consumers.</strong> Our business model is based on helping local businesses attract customers, not on charging you.</> 
        },
        { 
          q: "What exactly do the rewards include?", 
          a: <>Stays with <strong>full board</strong> (accommodation, breakfast, lunch and dinner) at selected 4 and 5-star hotels. Transport to the destination is on you, giving you total flexibility to choose how and when to travel.</> 
        },
        { 
          q: "Do my points expire?", 
          a: <>No. <strong>Your points are yours forever.</strong> Accumulate at your own pace, without pressure, and redeem whenever you decide.</> 
        },
        { 
          q: "How is it different from other discount platforms?", 
          a: <>In everything. Traditional coupon platforms charge huge commissions that squeeze businesses. <strong>With LokAlGrow, you pay the business owner directly.</strong> We reward your impact on the local economy, we don't extract value from it.</> 
        },
        { 
          q: "How do you verify my purchases?", 
          a: <>Our <strong>AI technology analyses the receipts</strong> you upload to validate your purchases securely and privately. It's fast, accurate and completely automatic.</> 
        }
      ],
      
      // Final CTA
      finalEyebrow: "Your Decision",
      finalTitle: <>The spending is already done.<br/>The holidays, not yet.</>,
      finalSub: "Join thousands of conscious consumers who have stopped giving away their loyalty and started recovering it.",
      finalCta: "Start Accumulating my Holidays",
      finalNote: "No credit card · No commitment · Always free"
    },
    fr: {
      // Hero
      heroEyebrow: "Club de Récompenses VIP",
      heroTitle: <>Saviez-vous que votre quartier<br/>vous doit un voyage à Paris<br/>ou aux Canaries ?</>,
      heroTitleAccent: "Il est temps de réclamer votre clé.",
      heroSub: <>Le consommateur moyen dépense entre <strong>3 000€ et 6 000€ par an</strong> dans les commerces locaux. Chez LokAlGrow, nous auditons votre fidélité et convertissons ces dépenses invisibles en séjours de luxe en pension complète dans des hôtels 4 et 5 étoiles. <em>Sans frais, sans intermédiaires.</em></>,
      ctaPrimary: "Réclamer mon Statut VIP — C'est Gratuit",
      ctaSecondary: "Découvrir comment ça marche",
      
      // Counter
      counterLabel: "Objectif de lancement",
      counterProgress: "acheteurs inscrits",
      counterTarget: "50 000",
      counterUnlock: "Les échanges de points s'activent à 50 000 acheteurs",
      counterNote: "Rejoignez-nous maintenant et accumulez des points dès le premier jour !",
      
      // Differentiator
      diffTitle: "Pas une autre plateforme de coupons.",
      diffSub: "Nous n'étouffons pas les commerces locaux. Vous payez directement le commerçant, nous récompensons votre impact communautaire.",
      
      // Spending Section
      spendingEyebrow: "Les mathématiques de votre fidélité",
      spendingTitle: <>Chaque euro dépensé<br/>localement est un actif.<br/><span>Arrêtez de le gaspiller.</span></>,
      spendingBody: <>Sans LokAlGrow, vos dépenses locales disparaissent sans laisser de trace. Des milliers d'euros par an qui s'évaporent sans rien vous donner en retour.<br/><br/><strong>Avec LokAlGrow, chaque achat construit votre prochain voyage.</strong> Vous accumulez de la valeur automatiquement et la transformez en expériences que vous ne vous permettriez pas normalement.</>,
      spendingCards: [
        { amount: "~1 500€/an", label: "Alimentation locale", desc: "Votre primeur, boucher, boulanger habituel" },
        { amount: "~900€/an", label: "Restaurants", desc: "Les dîners, cafés et repas du quartier" },
        { amount: "~480€/an", label: "Mode locale", desc: "Boutiques et commerces indépendants" },
        { amount: "~300€/an", label: "Services", desc: "Coiffeur, esthétique, bien-être" }
      ],
      spendingTotal: "+3 000€",
      spendingTotalLabel: "de dépenses annuelles que vous pourriez convertir en vacances",
      
      // Process
      processEyebrow: "Le Processus",
      processTitle: <>Trois étapes vers<br/>votre prochaine destination.</>,
      processSub: "Sans complications. Sans apps oubliées. Sans points qui expirent.",
      steps: [
        { 
          num: "01", 
          title: "Réservez votre Pass VIP", 
          desc: "Trouvez les commerces qui valorisent votre fidélité. Activez des offres exclusives jusqu'à 72 heures.",
          detail: "Chaque offre est une opportunité unique"
        },
        { 
          num: "02", 
          title: "Profitez de votre Quartier", 
          desc: "Payez directement au commerce. Sans intermédiaires, sans frais cachés. Votre argent va intégralement au commerçant.",
          detail: "Soutien direct au commerce local"
        },
        { 
          num: "03", 
          title: "Téléchargez votre Ticket", 
          desc: "Notre IA valide votre investissement local et vous rapproche de votre prochaine destination. Chaque achat compte.",
          detail: "La technologie à votre service"
        }
      ],
      
      // Rewards
      rewardsEyebrow: "Vos Récompenses",
      rewardsTitle: <>Où votre fidélité<br/>peut vous emmener.</>,
      rewardsSub: "Nous ne parlons pas de bons de 2€. Nous parlons d'expériences dont vous vous souviendrez toute votre vie.",
      rewardsNote: "Vous choisissez comment y arriver, nous nous occupons de votre séjour. Les récompenses incluent la pension complète dans des hôtels 4 et 5 étoiles sélectionnés. Vols non inclus.",
      rewards: [
        { 
          destination: "Paris", 
          nights: "3 Nuits",
          hotel: "Hôtel 4★ en pension complète",
          image: "https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=600&q=80",
          tag: "Le plus demandé"
        },
        { 
          destination: "Canaries", 
          nights: "3 Nuits",
          hotel: "Resort 4★ face à la mer",
          image: "https://images.unsplash.com/photo-1590523741831-ab7e8b8f9c7f?w=600&q=80",
          tag: "Paradis proche"
        },
        { 
          destination: "Europe", 
          nights: "7 Nuits",
          hotel: "Votre choix de destination",
          image: "https://images.unsplash.com/photo-1499856871958-5b9627545d1a?w=600&q=80",
          tag: "L'expérience ultime"
        }
      ],
      
      // Social Proof
      proofEyebrow: "La Philosophie",
      proofQuote: "\"En n'utilisant pas LokAlGrow, vous perdez l'argent que vous dépensez déjà de toute façon.\"",
      proofAuthor: "— Le principe qui change tout",
      
      // FAQ
      faqEyebrow: "FAQ",
      faqTitle: <>Transparence<br/>absolue.</>,
      faqs: [
        { 
          q: "C'est vraiment gratuit ?", 
          a: <>Absolument. <strong>LokAlGrow est et sera toujours 100% gratuit pour les consommateurs.</strong> Notre modèle économique repose sur l'aide aux commerces locaux pour attirer des clients, pas sur vous facturer.</> 
        },
        { 
          q: "Qu'incluent exactement les récompenses ?", 
          a: <>Des séjours en <strong>pension complète</strong> (hébergement, petit-déjeuner, déjeuner et dîner) dans des hôtels 4 et 5 étoiles sélectionnés. Le transport jusqu'à la destination est à votre charge, ce qui vous donne une flexibilité totale pour choisir comment et quand voyager.</> 
        },
        { 
          q: "Mes points expirent-ils ?", 
          a: <>Non. <strong>Vos points sont à vous pour toujours.</strong> Accumulez à votre rythme, sans pression, et échangez quand vous le décidez.</> 
        },
        { 
          q: "En quoi c'est différent des autres plateformes de réduction ?", 
          a: <>En tout. Les plateformes de coupons traditionnelles prélèvent d'énormes commissions qui étouffent les commerces. <strong>Avec LokAlGrow, vous payez directement le commerçant.</strong> Nous récompensons votre impact sur l'économie locale, nous n'en extrayons pas de valeur.</> 
        },
        { 
          q: "Comment vérifiez-vous mes achats ?", 
          a: <>Notre <strong>technologie IA analyse les tickets</strong> que vous téléchargez pour valider vos achats de manière sécurisée et privée. C'est rapide, précis et entièrement automatique.</> 
        }
      ],
      
      // Final CTA
      finalEyebrow: "Votre Décision",
      finalTitle: <>Les dépenses sont déjà faites.<br/>Les vacances, pas encore.</>,
      finalSub: "Rejoignez des milliers de consommateurs conscients qui ont arrêté de donner leur fidélité et ont commencé à la récupérer.",
      finalCta: "Commencer à Accumuler mes Vacances",
      finalNote: "Sans carte de crédit · Sans engagement · Toujours gratuit"
    }
  };

  // Determine content based on language
  const getContent = () => {
    if (lang === 'fr') return content.fr;
    if (lang === 'es' || isSpanish) return content.es;
    return content.en;
  };
  
  const c = getContent();

  return (
    <div className="vip-landing">
      {/* Landing Switcher */}
      <LandingSwitcher />
      
      {/* Hero */}
      <section className="vip-hero">
        <div className="vip-hero-bg">
          <div className="vip-hero-gradient" />
          <div className="vip-hero-pattern" />
        </div>
        
        <div className="vip-hero-content">
          <motion.div 
            className="vip-eyebrow"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Sparkles className="vip-eyebrow-icon" />
            {c.heroEyebrow}
          </motion.div>
          
          <motion.h1 
            className="vip-hero-title"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
          >
            {c.heroTitle}
          </motion.h1>
          
          <motion.p 
            className="vip-hero-accent"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
          >
            {c.heroTitleAccent}
          </motion.p>
          
          <motion.p 
            className="vip-hero-sub"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.3 }}
          >
            {c.heroSub}
          </motion.p>
          
          <motion.div 
            className="vip-hero-ctas"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.4 }}
          >
            <Link to="/register" className="vip-btn-primary" data-testid="vip-cta-primary">
              {c.ctaPrimary}
            </Link>
            <button 
              onClick={() => scrollToSection('proceso')} 
              className="vip-btn-secondary"
              data-testid="vip-how-it-works"
            >
              {c.ctaSecondary}
            </button>
          </motion.div>
        </div>
      </section>

      {/* Progress Counter - Unlock at 50K */}
      <section className="vip-counter">
        <div className="vip-counter-inner">
          <div className="vip-counter-badge">
            <Shield className="vip-counter-icon" />
          </div>
          <div className="vip-counter-content">
            <span className="vip-counter-label">{c.counterLabel}</span>
            <div className="vip-counter-progress-container">
              <div className="vip-counter-numbers">
                <span className="vip-counter-current">{userCount.toLocaleString()}</span>
                <span className="vip-counter-separator">/</span>
                <span className="vip-counter-target">{c.counterTarget}</span>
              </div>
              <span className="vip-counter-progress-label">{c.counterProgress}</span>
              <div className="vip-counter-bar">
                <div 
                  className="vip-counter-bar-fill" 
                  style={{ width: `${Math.min((userCount / 50000) * 100, 100)}%` }}
                />
              </div>
            </div>
            <p className="vip-counter-unlock">{c.counterUnlock}</p>
            <p className="vip-counter-note">{c.counterNote}</p>
          </div>
        </div>
      </section>

      {/* Differentiator */}
      <section className="vip-diff">
        <div className="vip-diff-inner">
          <h2 className="vip-diff-title">{c.diffTitle}</h2>
          <p className="vip-diff-sub">{c.diffSub}</p>
        </div>
      </section>

      {/* Spending Section */}
      <section className="vip-spending">
        <div className="vip-section-inner">
          <div className="vip-spending-grid">
            <div className="vip-spending-left">
              <span className="vip-eyebrow-small">{c.spendingEyebrow}</span>
              <h2 className="vip-spending-title">{c.spendingTitle}</h2>
              <p className="vip-spending-body">{c.spendingBody}</p>
            </div>
            
            <div className="vip-spending-right">
              <div className="vip-spending-cards">
                {c.spendingCards.map((card, i) => (
                  <div key={i} className="vip-spend-card">
                    <div className="vip-spend-amount">{card.amount}</div>
                    <div className="vip-spend-label">{card.label}</div>
                    <div className="vip-spend-desc">{card.desc}</div>
                  </div>
                ))}
              </div>
              
              <div className="vip-spending-total">
                <div className="vip-total-number">{c.spendingTotal}</div>
                <div className="vip-total-label">{c.spendingTotalLabel}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="vip-process" id="proceso">
        <div className="vip-section-inner">
          <span className="vip-eyebrow-small vip-center">{c.processEyebrow}</span>
          <h2 className="vip-section-title vip-center">{c.processTitle}</h2>
          <p className="vip-section-sub vip-center">{c.processSub}</p>
          
          <div className="vip-steps">
            {c.steps.map((step, i) => (
              <div key={i} className="vip-step">
                <div className="vip-step-num">{step.num}</div>
                <div className="vip-step-content">
                  <h3 className="vip-step-title">{step.title}</h3>
                  <p className="vip-step-desc">{step.desc}</p>
                  <span className="vip-step-detail">{step.detail}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Rewards */}
      <section className="vip-rewards">
        <div className="vip-section-inner">
          <span className="vip-eyebrow-small vip-center vip-gold">{c.rewardsEyebrow}</span>
          <h2 className="vip-section-title vip-center vip-white">{c.rewardsTitle}</h2>
          <p className="vip-section-sub vip-center">{c.rewardsSub}</p>
          
          <div className="vip-rewards-grid">
            {c.rewards.map((reward, i) => (
              <div key={i} className="vip-reward-card">
                <div className="vip-reward-image">
                  <img src={reward.image} alt={reward.destination} />
                  <span className="vip-reward-tag">{reward.tag}</span>
                </div>
                <div className="vip-reward-content">
                  <h3 className="vip-reward-destination">{reward.destination}</h3>
                  <p className="vip-reward-nights">{reward.nights}</p>
                  <p className="vip-reward-hotel">{reward.hotel}</p>
                </div>
              </div>
            ))}
          </div>
          
          <p className="vip-rewards-note">
            <Star className="vip-note-icon" />
            {c.rewardsNote}
          </p>
        </div>
      </section>

      {/* Social Proof */}
      <section className="vip-proof">
        <div className="vip-proof-inner">
          <span className="vip-eyebrow-small">{c.proofEyebrow}</span>
          <blockquote className="vip-proof-quote">{c.proofQuote}</blockquote>
          <p className="vip-proof-author">{c.proofAuthor}</p>
        </div>
      </section>

      {/* FAQ */}
      <section className="vip-faq">
        <div className="vip-section-inner">
          <span className="vip-eyebrow-small">{c.faqEyebrow}</span>
          <h2 className="vip-section-title">{c.faqTitle}</h2>
          
          <div className="vip-faq-list">
            {c.faqs.map((faq, i) => (
              <div 
                key={i} 
                className={`vip-faq-item ${openFaq === i ? 'open' : ''}`}
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
              >
                <div className="vip-faq-q">
                  <span>{faq.q}</span>
                  <ChevronDown className={`vip-faq-chevron ${openFaq === i ? 'rotate' : ''}`} />
                </div>
                {openFaq === i && (
                  <motion.div 
                    className="vip-faq-a"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                  >
                    {faq.a}
                  </motion.div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="vip-final">
        <div className="vip-final-bg" />
        <div className="vip-final-content">
          <span className="vip-eyebrow-small vip-gold">{c.finalEyebrow}</span>
          <h2 className="vip-final-title">{c.finalTitle}</h2>
          <p className="vip-final-sub">{c.finalSub}</p>
          <Link to="/register" className="vip-btn-final" data-testid="vip-final-cta">
            {c.finalCta}
          </Link>
          <p className="vip-final-note">{c.finalNote}</p>
        </div>
      </section>

      {/* Legal Footer */}
      <footer className="bg-gray-900 border-t border-gray-800 py-8">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <img src={FAVICON} alt="LokAlGrow" className="w-8 h-8 rounded-lg" />
              <span className="text-gray-400 text-sm">© {new Date().getFullYear()} LokAlGrow</span>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-4 text-sm">
              <Link to="/privacy" className="text-gray-400 hover:text-emerald-400 transition-colors">
                {lang === 'es' ? 'Privacidad' : lang === 'fr' ? 'Confidentialité' : 'Privacy'}
              </Link>
              <Link to="/terms" className="text-gray-400 hover:text-emerald-400 transition-colors">
                {lang === 'es' ? 'Términos' : lang === 'fr' ? 'Conditions' : 'Terms'}
              </Link>
              <Link to="/cookies" className="text-gray-400 hover:text-emerald-400 transition-colors">
                Cookies
              </Link>
              <Link to="/legal-notice" className="text-gray-400 hover:text-emerald-400 transition-colors">
                {lang === 'es' ? 'Aviso Legal' : lang === 'fr' ? 'Mentions Légales' : 'Legal Notice'}
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ShopperLandingPage;
